import { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import type { Course } from "../types"
import { CourseStatusBadge } from "./CourseStatusBadge"
import { useTranslation } from "react-i18next"

interface CourseCardProps {
  course: Course
  onPublish: (id: string) => void
  onArchive: (id: string) => void
  onDelete: (course: Course) => void
  onEdit: (course: Course) => void
}

const MODE_LABELS: Record<string, string> = {
  free: "Libre",
  rag: "RAG",
  hybrid: "Hybride",
}

const MODE_BADGE_CLASS: Record<string, string> = {
  free: "bg-mindeia-primary-light text-mindeia-primary",
  rag: "bg-mindeia-primary-light text-mindeia-primary",
  hybrid: "bg-surface-100 text-neutral-700",
}

const STATUS_BORDER_CLASS: Record<string, string> = {
  draft: "border-l-neutral-300",
  active: "border-l-mindeia-success",
  archived: "border-l-neutral-600",
}

export function CourseCard({ course, onPublish, onArchive, onDelete, onEdit }: CourseCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function onOutsideClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", onOutsideClick)
    return () => document.removeEventListener("mousedown", onOutsideClick)
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) return
    function onEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false)
    }
    document.addEventListener("keydown", onEscape)
    return () => document.removeEventListener("keydown", onEscape)
  }, [menuOpen])

  const borderClass = STATUS_BORDER_CLASS[course.status] ?? "border-l-neutral-300"
  const showDocCount = course.mode === "rag" || course.mode === "hybrid"

  return (
    <article
      className={[
        "relative flex flex-col gap-3 rounded-xl border border-surface-200 border-l-4 bg-white p-5",
        "transition-shadow hover:shadow-md",
        borderClass,
      ].join(" ")}
    >
      {/* Card header */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 font-semibold leading-snug text-neutral-900">
            {course.title}
          </h3>
          <p className="mt-0.5 truncate text-sm text-neutral-600">{course.subject}</p>
        </div>

        {/* Context menu */}
        <div ref={menuRef} className="relative z-10 flex-shrink-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setMenuOpen((v) => !v)
            }}
            aria-label={t("courses.card_options")}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            className="flex h-11 w-11 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-surface-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="currentColor"
              aria-hidden="true"
            >
              <circle cx="9" cy="3.5" r="1.5" />
              <circle cx="9" cy="9" r="1.5" />
              <circle cx="9" cy="14.5" r="1.5" />
            </svg>
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-12 z-20 min-w-44 rounded-xl border border-surface-200 bg-white py-1 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onEdit(course)
                }}
                className="flex w-full items-center px-4 py-2.5 text-left text-sm text-neutral-700 transition-colors hover:bg-surface-50 focus:outline-none focus-visible:bg-surface-50"
                style={{ minHeight: "44px" }}
              >
                {t('courses.edit')}
              </button>

              {course.status === "draft" && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation()
                    setMenuOpen(false)
                    onPublish(course.id)
                  }}
                  className="flex w-full items-center px-4 py-2.5 text-left text-sm text-mindeia-success transition-colors hover:bg-surface-50 focus:outline-none focus-visible:bg-surface-50"
                  style={{ minHeight: "44px" }}
                >
                  {t('courses.publish')}
                </button>
              )}

              {course.status === "active" && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation()
                    setMenuOpen(false)
                    onArchive(course.id)
                  }}
                  className="flex w-full items-center px-4 py-2.5 text-left text-sm text-neutral-700 transition-colors hover:bg-surface-50 focus:outline-none focus-visible:bg-surface-50"
                  style={{ minHeight: "44px" }}
                >
                  {t('courses.archive')}
                </button>
              )}

              <hr className="my-1 border-surface-100" />

              <button
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation()
                  setMenuOpen(false)
                  onDelete(course)
                }}
                className="flex w-full items-center px-4 py-2.5 text-left text-sm text-mindeia-error transition-colors hover:bg-mindeia-error-light focus:outline-none focus-visible:bg-mindeia-error-light"
                style={{ minHeight: "44px" }}
              >
                {t('common.delete')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Badges row */}
      <div className="relative flex flex-wrap items-center gap-1.5">
        <span
          className={[
            "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
            MODE_BADGE_CLASS[course.mode] ?? "bg-surface-100 text-neutral-700",
          ].join(" ")}
        >
          {MODE_LABELS[course.mode] ?? course.mode}
        </span>

        <CourseStatusBadge status={course.status} size="sm" />

        {showDocCount && (
          <span className="inline-flex items-center gap-1 rounded-full bg-surface-100 px-2.5 py-0.5 text-xs font-medium text-neutral-600">
            <svg
              width="11"
              height="12"
              viewBox="0 0 11 12"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M1.5 1.5h5.5l2 2v8a.5.5 0 01-.5.5h-7a.5.5 0 01-.5-.5v-9.5a.5.5 0 01.5-.5z"
                stroke="currentColor"
                strokeWidth="1.2"
                fill="none"
              />
              <path d="M7 1.5V4h2.5" stroke="currentColor" strokeWidth="1.2" />
            </svg>
            {course.documentsCount} doc{course.documentsCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Invisible overlay button — last child so it stacks above all z-auto content, below z-10 menu */}
      <button
        type="button"
        aria-label={`Voir le cours : ${course.title}`}
        onClick={() => navigate(`/courses/${course.id}`)}
        className="absolute inset-0 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-mindeia-primary focus-visible:ring-offset-2"
      />
    </article>
  )
}
